import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon/Icon';
import type { IconName } from '@shared/ui/icon/Icon';

export interface ActionItem {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  /** Style destructeur (rouge). */
  danger?: boolean;
  disabled?: boolean;
}

export interface ActionsMenuProps {
  /** Libellé du bouton déclencheur (accessibilité). */
  triggerLabel: string;
  items: ActionItem[];
}

interface MenuPosition {
  top?: number;
  bottom?: number;
  right: number;
}

/**
 * Menu d'actions déroulant (⋯), accessible clavier (Échap ferme, clic
 * extérieur ferme). Rendu via portail dans `document.body` en position
 * `fixed` pour ne pas être rogné par un ancêtre `overflow` (ex. table
 * scrollable). S'ouvre vers le haut si proche du bas du viewport.
 */
export function ActionsMenu({ triggerLabel, items }: ActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openUp = window.innerHeight - rect.bottom < 200;
    setPosition({
      right: window.innerWidth - rect.right,
      ...(openUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const toggle = () => {
    setOpen((value) => !value);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel}
        onClick={toggle}
        className="inline-flex size-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
      >
        <Icon name="dots" size={18} />
      </button>
      {open && position
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ top: position.top, bottom: position.bottom, right: position.right }}
              className="fixed z-50 min-w-44 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
            >
              {items.map((item) => (
                <MenuButton key={item.label} item={item} onDone={() => setOpen(false)} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function MenuButton({ item, onDone }: { item: ActionItem; onDone: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={item.disabled}
      onClick={() => {
        item.onSelect();
        onDone();
      }}
      className={cn(
        'flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] transition-colors disabled:opacity-50',
        item.danger
          ? 'font-medium text-red-600 hover:bg-red-50'
          : 'text-slate-700 hover:bg-slate-100',
      )}
    >
      {item.icon ? <Icon name={item.icon} size={15} /> : null}
      {item.label}
    </button>
  );
}
